import React, {useEffect} from 'react';
import {Navigate, Route, Routes, useNavigate} from 'react-router-dom';
import {getStacksGroups} from '../utils/router';
import paths from '../config/paths';
import sharedRoutes from './routes/sharedRoutes';
import Icon from '../components/icon';
import {tr} from '../translation';
import styled, {useTheme} from 'styled-components';
import {Theme} from '../theme/lightTheme';
import MainLayout from '../screens/mainLayout';
import {isSmallHeight} from '../utils/responsive';
import Researches from '../screens/content/researches';

const ScrapeArticle = React.lazy(
  () => import('../screens/content/scrapeArticles'),
);
const DashboardLogin = React.lazy(() => import('../screens/auth/login'));

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 100%;
  min-height: 100vh;
  filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.2));
  backdrop-filter: blur(5px);
`;
const BlurContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    360deg,
    ${({theme}) => theme.secondary + 'AA'} -68.52%,
    ${({theme}) => theme.primary + 'AA'} 122.02%
  );
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4);
  border-radius: 26px;
  padding: ${isSmallHeight ? 3 : 5}rem;
  aspect-ratio: 1;
`;
const LoadingText = styled.p`
  padding-top: 1.5rem;
  font-size: ${({theme}) => theme.text.s7};
  font-weight: bold;
  letter-spacing: 1px;
  color: ${({theme}) => theme.text.light};
`;

export const AppLoading = () => {
  const theme = useTheme() as Theme;
  return (
    <MainLayout>
      <Container>
        <BlurContainer>
          <Icon
            type={'SVG'}
            name={'loader'}
            color={theme.text.light}
            size={theme.text.s2}
          />
          <LoadingText>{tr('app.loading')}</LoadingText>
        </BlurContainer>
      </Container>
    </MainLayout>
  );
};

/**
 * Select and render the appropriate router (either 'auth' or 'content').
 * If router value is empty, render a loading indicator.
 *
 * @returns {JSX.Element} The JSX element representing the selected router.
 */
const RouteSwitcher = ({
  router,
}: {
  router: 'contentScraping' | 'contentResearches' | 'auth' | 'firstLogin' | '';
  isHaveDashboardAccess?: boolean;
}): JSX.Element => {
  const navigate = useNavigate();

  useEffect(() => {
    navigate(
      router === 'contentScraping'
        ? paths.dashboard.scrapeArticle
        : router === 'contentResearches'
          ? paths.dashboard.researches
          : paths.dashboard.login,
    );
  }, [router]);
  if (router === '') {
    return <AppLoading />;
  }
  return (
    <React.Suspense fallback={<AppLoading />}>
      <Routes>
        <Route element={<MainLayout />}>
          <Route
            path={paths.dashboard.scrapeArticle}
            element={
              router === 'auth' ? (
                <Navigate to={paths.dashboard.login} />
              ) : (
                <ScrapeArticle />
              )
            }
          />
          <Route
            path={paths.dashboard.researches}
            element={
              router === 'auth' ? (
                <Navigate to={paths.dashboard.login} />
              ) : (
                <Researches />
              )
            }
          />
          <Route
            path={paths.dashboard.login}
            element={
              router === 'contentScraping' || router === 'contentResearches' ? (
                <Navigate
                  to={
                    router === 'contentScraping'
                      ? paths.dashboard.scrapeArticle
                      : paths.dashboard.researches
                  }
                />
              ) : (
                <DashboardLogin />
              )
            }
          />

          {getStacksGroups(sharedRoutes)}
        </Route>
      </Routes>
    </React.Suspense>
  );
};

export default RouteSwitcher;
